import React from 'react';

const AshtakavargaTable = ({ bavData, title }) => {
  if (!bavData) return null;

  const { per_sign, per_house, total } = bavData;

  return (
    <div className="bg-gradient-to-br from-purple-900/30 to-indigo-900/30 rounded-xl p-6 border border-purple-500/30">
      <h4 className="text-purple-300 text-xl mb-4 font-medium">{title}</h4>
      
      {/* Sign View */}
      {per_sign && (
        <div className="mb-6">
          <h5 className="text-cyan-400 text-lg mb-3">Bindus per Sign</h5>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-purple-900/50 border-b border-purple-700/50">
                  <th className="text-left p-3 text-purple-300">Sign</th>
                  <th className="text-center p-3 text-purple-300">Bindus</th>
                </tr>
              </thead>
              <tbody>
                {per_sign.map((signData, idx) => (
                  <tr key={idx} className="border-b border-white/10 hover:bg-white/5">
                    <td className="p-3 text-cyan-300 font-medium">{signData.sign}</td>
                    <td className="p-3 text-center text-yellow-400 font-bold text-lg">{signData.bindus}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-purple-900/50 border-t-2 border-purple-500/50">
                  <td className="p-3 text-purple-200 font-bold">Total</td>
                  <td className="p-3 text-center text-yellow-300 font-bold text-lg">{total}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* House View (if available) */}
      {per_house && (
        <div>
          <h5 className="text-cyan-400 text-lg mb-3">Bindus per House</h5>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-indigo-900/50 border-b border-indigo-700/50">
                  <th className="text-left p-3 text-indigo-300">House</th>
                  <th className="text-left p-3 text-indigo-300">Sign</th>
                  <th className="text-center p-3 text-indigo-300">Bindus</th>
                </tr>
              </thead>
              <tbody>
                {per_house.map((houseData, idx) => (
                  <tr key={idx} className="border-b border-white/10 hover:bg-white/5">
                    <td className="p-3 text-yellow-400 font-medium">House {houseData.house}</td>
                    <td className="p-3 text-cyan-300">{houseData.sign}</td>
                    <td className="p-3 text-center text-yellow-400 font-bold text-lg">{houseData.bindus}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

// Component for displaying all planets' BAV in a grid
export const AllPlanetsBavGrid = ({ bavAllData }) => {
  if (!bavAllData) return null;

  const planets = ['Sun', 'Moon', 'Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn'];
  const signs = ['Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo', 
                 'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces'];

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="bg-purple-900/50 border-b border-purple-700/50">
            <th className="text-left p-2 text-purple-300 sticky left-0 bg-purple-900/50">Sign</th>
            {planets.map(planet => (
              <th key={planet} className="text-center p-2 text-purple-300">{planet}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {signs.map((sign, signIdx) => (
            <tr key={signIdx} className="border-b border-white/10 hover:bg-white/5">
              <td className="p-2 text-cyan-300 font-medium sticky left-0 bg-slate-900/80">{sign}</td>
              {planets.map(planet => {
                const planetData = bavAllData[planet];
                const bindus = planetData?.per_sign?.[signIdx]?.bindus || 0;
                return (
                  <td key={planet} className="p-2 text-center text-yellow-400 font-bold">
                    {bindus}
                  </td>
                );
              })}
            </tr>
          ))}
          <tr className="bg-purple-900/50 border-t-2 border-purple-500/50 font-bold">
            <td className="p-2 text-purple-200">Total</td>
            {planets.map(planet => {
              const planetData = bavAllData[planet];
              const total = planetData?.total || 0;
              return (
                <td key={planet} className="p-2 text-center text-yellow-300 font-bold">
                  {total}
                </td>
              );
            })}
          </tr>
        </tbody>
      </table>
    </div>
  );
};

// Component for displaying SAV
export const SavDisplay = ({ savData }) => {
  if (!savData) return null;

  const { sav, sav_house } = savData;

  return (
    <div className="space-y-6">
      {/* SAV by Sign */}
      {sav && (
        <div className="bg-gradient-to-br from-cyan-900/30 to-blue-900/30 rounded-xl p-6 border border-cyan-500/30">
          <h4 className="text-cyan-300 text-xl mb-4 font-medium">
            Sarvashtakavarga (SAV) - Total of All 7 Planets
          </h4>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {sav.per_sign?.map((signData, idx) => (
              <div key={idx} className="bg-white/5 rounded-lg p-4 border border-cyan-500/20">
                <p className="text-cyan-300 font-medium mb-2">{signData.sign}</p>
                <p className="text-yellow-400 text-3xl font-bold">{signData.bindus}</p>
              </div>
            ))}
          </div>
          <div className="mt-4 bg-yellow-500/10 rounded-lg p-4 border border-yellow-500/30">
            <p className="text-yellow-300 text-lg">
              <span className="font-medium">Total SAV:</span> 
              <span className="text-2xl font-bold ml-2">{sav.total}</span>
              <span className="text-white/60 ml-2">(Expected: 337)</span>
            </p>
          </div>
        </div>
      )}

      {/* SAV by House */}
      {sav_house && (
        <div className="bg-gradient-to-br from-indigo-900/30 to-purple-900/30 rounded-xl p-6 border border-indigo-500/30">
          <h4 className="text-indigo-300 text-xl mb-4 font-medium">SAV by House</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {sav_house.per_house?.map((houseData, idx) => (
              <div key={idx} className="bg-white/5 rounded-lg p-4 border border-indigo-500/20">
                <p className="text-indigo-300 font-medium mb-1">House {houseData.house}</p>
                <p className="text-cyan-300 text-xs mb-2">{houseData.sign}</p>
                <p className="text-yellow-400 text-2xl font-bold">{houseData.bindus}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AshtakavargaTable;
